import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import { MultiSelect, Select } from '@webapp/common/components/select';
import { CssQuestionType } from '@webapp/common/components/survey-custom';
import { useMobile } from '@webapp/common/hooks/use-mobile';
import { PLACEHOLDER } from '@webapp/common/resources/fillers';
import { AnswerType } from '@webapp/common/resources/survey';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import css from './dropdown.css';

export const Dropdown: VFC<SurveyQuestion> = ({
    actor,
    question: {
        answersActors,
        responseActor,
        params: { dropdownType, maxLength },
    },
}) => {
    const [, updateRerenderHack] = useState(1);
    const isMobile = useMobile();
    const { value, questionId } = useSelector(responseActor, (r) => r.context);

    useEffect(() => {
        const sub = () => updateRerenderHack((prev) => (prev + 1) % 10);
        const unsubs = answersActors.map((actor) => {
            return actor.subscribe(sub);
        });
        return () => {
            unsubs.forEach((unsub) => unsub.unsubscribe());
        };
    }, []);
    const questions = useStateSelector((s) => s.questions);
    const options = answersActors
        .filter(
            (act) =>
                act.getSnapshot().context.hidden !== true && act.getSnapshot().context.hiddenByAnswersHide !== true,
        )
        .map((answerActor) => {
            const answer = answerActor.getSnapshot().context;
            const el = document.createElement('span');
            if (answer.forwardParent) {
                let forwardParentAnswer: QuestionAnswerActor | null = null;
                questions.forEach((q) => {
                    const founded = q.getSnapshot().context.answersActors.find((a) => {
                        return a.getSnapshot().context.id === answer.forwardParent;
                    });
                    if (founded) {
                        forwardParentAnswer = founded;
                    }
                });
                if (forwardParentAnswer && forwardParentAnswer.getSnapshot().context.type === AnswerType.SELF) {
                    const sub = () => updateRerenderHack((prev) => (prev + 1) % 10);
                    forwardParentAnswer.subscribe(sub);
                    el.innerHTML = forwardParentAnswer
                        .getSnapshot()
                        .context.response.getSnapshot()
                        .context.extra?.toString();
                } else {
                    el.innerHTML = answer.value[0];
                }
            } else {
                el.innerHTML = answer.value[0];
            }
            const label = el.innerText;
            return { label, value: answer.id };
        });
    const ctx = useContext(CustomStylesCtx);
    const customStyles = useMemo(
        () => ({
            ...ctx,
            select: {
                ...ctx.select,
                root: { ...ctx.select.root, maxWidth: '40vh', width: isMobile ? '100%' : '400px' },
            },
        }),
        [ctx, isMobile],
    );

    const change = useCallback(
        (value) => {
            responseActor.send({ type: 'CHANGE', value, questionId });
            actor.send({ type: 'QUESTION_RESPONSE_CHANGE', questionId, value });
        },
        [responseActor, questionId],
    );

    const onMultiChange = useCallback(
        (v: Array<PrimitiveValue>) => {
            const exception = answersActors.find((answerActor) => {
                const a = answerActor.getSnapshot().context;
                return a.exception && a.response.getSnapshot().context.value;
            });

            const needToUnpushException =
                exception && (v.length > 1 || (v.length === 1 && exception.id.toString() === v[0].toString()));

            answersActors.forEach((answerActor) => {
                const a = answerActor.getSnapshot().context;
                if (needToUnpushException && a.exception) {
                    a.response.send({ type: 'SET', value: false });
                } else {
                    a.response.send({ type: 'SET', value: v.includes(a.id.toString()) });
                }
            });

            void change('');
            updateRerenderHack((prev) => (prev + 1) % 10);
        },
        [change],
    );
    const items = answersActors
        .filter((a) => a.getSnapshot().context.response.getSnapshot().context.value)
        .map((a) => a.getSnapshot().context.id.toString());

    useEffect(() => {
        if (dropdownType === 'multi') {
            const availiableSelectedOptions = items.filter((item) =>
                options.find((opt) => opt.value.toString() === item.toString()),
            );
            if (availiableSelectedOptions.length !== items.length) {
                onMultiChange(availiableSelectedOptions);
            }
        } else {
            if (value && !options.find((opt) => opt.value.toString() === value?.toString())) {
                change('');
            }
        }
    }, [options, items]);

    return (
        <div className={cn(CssQuestionType.QUESTION, CssQuestionType.DROPDOWN, css.dropdown)}>
            <CustomStylesCtx.Provider value={customStyles}>
                {dropdownType === 'multi' ? (
                    <MultiSelect
                        searchable={true}
                        classNames={{ control: css.control }}
                        options={options.map((o) => ({ ...o, value: o.value.toString() }))}
                        placeholder={PLACEHOLDER.EMPTY}
                        value={items}
                        onChange={(newValues) => onMultiChange(newValues.map((item) => item.value))}
                        max={maxLength ? maxLength : undefined}
                    />
                ) : (
                    <Select
                        searchable={true}
                        classNames={{ control: css.control }}
                        options={options}
                        placeholder={PLACEHOLDER.EMPTY}
                        value={value as number}
                        onChange={change}
                    />
                )}
            </CustomStylesCtx.Provider>
        </div>
    );
};
Dropdown.displayName = 'Dropdown';
